






































































































import Vue, { PropType } from "vue";
import iwTabsLanguageSwitcherButton from "./TabsLanguageSwitcherButton.vue";
import iwTextarea from "@/encoding/components/buildingBlocks/Textarea.vue";
import { Trans } from "@/shared/scripts/helpers/translation/translation";
import { v4 as uuidv4 } from "uuid";

import { Textarea } from "@/shared/interfaces/Textarea.interface";
import { Language } from "@/shared/interfaces/TabsLanguageSwitcher.interface";
import { isTitleTooLong, TITLE_MAX_LENGTH } from "@/shared/scripts/helpers/titleValidation";
import { mapGetters } from "vuex";

export default Vue.extend({
    components: {
        iwTextarea: iwTextarea,
        iwTabsLanguageSwitcherButton: iwTabsLanguageSwitcherButton
    },
    props: {
        labelId: {
            type: String,
            required: true
        },
        languages: {
            type: Array as PropType<Language[]>,
            required: true,
        },
        maxLength: {
            type: Number,
            required: false,
            default: 1500
        },
        hasTitle: {
            type: Boolean,
            required: false,
            default: true
        },
        hasDescription: {
            type: Boolean,
            required: false,
            default: true
        },
        titleLabel: {
            type: String,
            required: false,
            default: ""
        },
        errorMessage: {
            type: String,
            required: false,
            default: null
        },
        titlePlaceholder: {
            type: String,
            required: false,
            default: ""
        },
        descriptionLabel: {
            type: String,
            required: false,
            default: ""
        },
        descriptionPlaceholder: {
            type: String,
            required: false,
            default: ""
        },
        storeValueTitle: {
            type: String,
            required: false,
            default: ""
        },
        storeValueDescription: {
            type: String,
            required: false,
            default: ""
        },
    },

    data: function() {
        return {
            uuid: uuidv4() as string,
            currentLanguage: "en" as string,
        };
    },

    computed: {
        ...mapGetters("FormModule", ["getValue", "getErrorObjectByPath"]),

        titleMaxLength: function(): number {
            return TITLE_MAX_LENGTH;
        },

        title: function(): Record<string, string> {
            return this.getValue(this.storeValueTitle) ?? {};
        },

        description: function(): Record<string, string> {
            return this.getValue(this.storeValueDescription) ?? {};
        },

        tabsButtons: function(): HTMLCollection {
            return this.$el?.getElementsByClassName("tab-language__button");
        },

        currentTab: function(): HTMLButtonElement {
            let switcher = (this.$refs[`languageSwitcher-${this.currentLanguage}-${this.uuid}`] as Vue[])[0];
            return switcher.$el as HTMLButtonElement;
        },

        currentTabIndex: function(): number {
            return parseInt(this.currentTab.dataset.index as string);
        },
    },

    created: function() {
        this.currentLanguage = Trans.currentLocale;
    },

    methods: {
        titleLength: function(locale: string): number {
            return (this.title[locale] ?? "").length;
        },

        // The client-side length check wins over the API one: it stays visible while the title is
        // still too long, whereas the API warning is dropped by FormModule on the next keystroke.
        titleError: function(locale: string): string|null {
            if (!this.hasTitle || !this.storeValueTitle) {
                return null;
            }

            if (isTitleTooLong(this.title[locale])) {
                // Same key the API returns for this, so the message is already translated.
                return this.$t(`INVALID_PROPERTY_TITLE_${locale.toUpperCase()}`) as string;
            }

            return this.getErrorObjectByPath(`${this.storeValueTitle}.${locale}`);
        },

        setTitle: function (title: string) {
            this.$store.dispatch("FormModule/addValue", {
                path: `${this.storeValueTitle}.${this.currentLanguage}`,
                value: title,
            });
        },

        clearDescriptions: function() {
            if (!this.storeValueDescription) return;

            this.languages.forEach(({ prefix }) => {
                const path = `${this.storeValueDescription}.${prefix}`;
                this.$store.commit("FormModule/deleteValue", path);

                // reset the component's value in the UI
                const descriptionComponent = ((this.$refs[`text-description-${prefix}-${this.uuid}`] as Vue[])[0] as Textarea);
                descriptionComponent?.reset("");
            });
        },

        emitLiveDescription: function(value: string) {
            this.$emit("description-live-update", {
                locale: this.currentLanguage,
                value: value,
            });
        },

        setDescription: function (localeOrValue: string, maybeValue?: unknown) {
            const hasLocaleArgument = typeof maybeValue !== "undefined";
            const locale = hasLocaleArgument ? localeOrValue : this.currentLanguage;
            const value = hasLocaleArgument ? maybeValue : localeOrValue;

            const path = `${this.storeValueDescription}.${locale}`;

            if (value === "") {
                return this.$store.commit("FormModule/deleteValue", path);
            }

            this.$store.dispatch("FormModule/addValue", {
                path: path,
                value: value,
            });
        },

        // Check user content validity for a given locale.
        validateLocaleContent: function(locale: string): boolean {
            return this.hasTitle
                ? !!this.title[locale] && !!this.description[locale]
                : !!this.description[locale];
        },

        changeLanguage: function(prefix: string) {
            this.currentLanguage = prefix;

            const inputTitle = this.$refs[`input-title-${prefix}-${this.uuid}`] as HTMLInputElement;
            if(inputTitle) {
                inputTitle.value = this.title[prefix];
            }

            const descriptionComponent = ((this.$refs[`text-description-${prefix}-${this.uuid}`] as Vue[])[0] as Textarea);
            descriptionComponent?.reset(this.description[prefix]);

            this.$emit("language-selected", prefix);
        },

        goToTab: function(index: number) {

            // loop index when out of bounds
            index = index < 0
                ? this.languages.length - 1
                : (index >= this.languages.length ? 0 : index);

            const targetTab = this.tabsButtons.item(index) as HTMLButtonElement;
            targetTab.focus();

            this.changeLanguage(targetTab.dataset.lang as string);
        },
    },
});
